import React from "react";
import {
  FaUndo,
  FaExchangeAlt,
  FaQuestionCircle,
  FaBoxOpen,
  FaMoneyBillWave,
  FaBan,
} from "react-icons/fa";
import { Link } from "react-router-dom";

const ReturnAndExchangePolicy = () => {
  return (
    <div className=" py-3 mx-auto bg-white shadow-xl rounded-2xl overflow-hidden">
      {/* Header with accent */}

      <div className="p-8 sm:p-10">
        <header className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            Return & Exchange Policy
          </h1>
          <div className="w-20 h-1 bg-[#C23F00] mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg max-w-xl mx-auto">
            Your satisfaction is our priority. Here's how we make returns and
            exchanges easy.
          </p>
        </header>

        {/* Policy Sections */}
        <div className="space-y-10">
          {/* Eligibility */}
          <div className="flex flex-col sm:flex-row gap-6 p-6 bg-orange-50 rounded-xl">
            <div className="flex-shrink-0">
              <div className="w-14 h-14 rounded-full bg-[#C23F00] text-white flex items-center justify-center">
                <FaUndo className="text-xl" />
              </div>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">
                Return Eligibility
              </h2>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-[#C23F00] mr-2">•</span>
                  <span>
                    Return within{" "}
                    <strong className="text-gray-900">7 days</strong> of
                    delivery
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#C23F00] mr-2">•</span>
                  <span>
                    Items must be{" "}
                    <strong className="text-gray-900">unused, undamaged</strong>
                    , in original packaging
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#C23F00] mr-2">•</span>
                  <span>
                    Only{" "}
                    <strong className="text-gray-900">
                      defective, damaged, or incorrect
                    </strong>{" "}
                    items are eligible
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Non-returnable Items */}
          <div className="flex flex-col sm:flex-row gap-6 p-6 bg-gray-50 rounded-xl">
            <div className="flex-shrink-0">
              <div className="w-14 h-14 rounded-full bg-gray-700 text-white flex items-center justify-center">
                <FaBan className="text-xl" />
              </div>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">
                Non-returnable Items
              </h2>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-gray-700 mr-2">•</span>
                  <span>Customized or personalized items</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gray-700 mr-2">•</span>
                  <span>Clearance or "Final Sale" items</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gray-700 mr-2">•</span>
                  <span>Used or washed products</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gray-700 mr-2">•</span>
                  <span>Products without original tags</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Process */}
          <div className="flex flex-col sm:flex-row gap-6 p-6 bg-blue-50 rounded-xl">
            <div className="flex-shrink-0">
              <div className="w-14 h-14 rounded-full bg-blue-600 text-white flex items-center justify-center">
                <FaExchangeAlt className="text-xl" />
              </div>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">
                Return & Exchange Process
              </h2>
              <ol className="space-y-4">
                {[
                  "Go to your order history and select the item",
                  "Fill out the request form with a valid reason",
                  "We'll verify and schedule a pickup if eligible",
                  "After inspection, we'll process your refund or exchange",
                ].map((step, index) => (
                  <li key={index} className="flex items-start">
                    <span className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mr-3 font-medium">
                      {index + 1}
                    </span>
                    <span className="text-gray-700">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {/* Refund */}
          <div className="flex flex-col sm:flex-row gap-6 p-6 bg-green-50 rounded-xl">
            <div className="flex-shrink-0">
              <div className="w-14 h-14 rounded-full bg-green-600 text-white flex items-center justify-center">
                <FaMoneyBillWave className="text-xl" />
              </div>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">
                Refund Information
              </h2>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">•</span>
                  <span>
                    Refunds take{" "}
                    <strong className="text-gray-900">5-7 business days</strong>{" "}
                    after we receive your return
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">•</span>
                  <span>
                    Amount will be credited to your original payment method
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">•</span>
                  <span>
                    Shipping charges are non-refundable (except for faulty
                    items)
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Contact & Note */}
        <div className="mt-12">
          <div className="p-6 bg-indigo-50 rounded-xl">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center">
                <FaQuestionCircle className="text-lg" />
              </div>
              Need Help With Your Return?
            </h2>
            <p className="text-gray-700 mb-4">
              Our customer service team is happy to assist you with any
              questions about returns or exchanges.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="inline-flex items-center justify-center px-5 py-3 bg-[#C23F00] text-white rounded-lg hover:bg-[#a53700] transition-colors"
              >
                Email Support
              </Link>

              <Link
                to="/contact"
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="inline-flex items-center justify-center px-5 py-3 border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Contact Form
              </Link>
            </div>
          </div>

          <div className="mt-6 p-4 bg-gray-100 rounded-lg">
            <p className="text-sm text-gray-600 italic">
              <strong>Note:</strong> We reserve the right to deny returns that
              don't meet our policy criteria. For hygienic reasons, certain
              products may not be eligible for return once opened.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReturnAndExchangePolicy;
